import { Spin } from 'antd'
import React, { ComponentProps, useEffect } from 'react'
import { Bar } from 'react-chartjs-2'
import { useInflowAgeGroupValues, useMunicipalityName } from '../../../../api/benchmarking'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { getFontSize } from '../../util/fontSize'
import { getColorByIndex } from '../../util/colors'
import { BenchmarkingLegend } from '../../components/BenchmarkingLegend'
import { BORDER_WIDTH } from '../../util/constants'
import { baseGraphOptions } from '../../util/baseGraphOptions'
import { EmptySelector } from '../../components/EmptySelector'
import { STATDB_NAME, NO_COMPARISON } from '../../util/names'

export const InflowAgeGroup = ({ text }: { text: React.ReactNode }) => {
  const { data: municipalityName, isLoading: isMunicipalityNameLoading } = useMunicipalityName()
  const { data, isLoading, mutate } = useInflowAgeGroupValues()

  useEffect(() => {
    mutate(undefined)
  }, [])

  if (isLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} />
  }

  if (!data) {
    return <></>
  }

  const graphOptions: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${Math.round(context.parsed.y)}%`
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          font: {
            size: getFontSize('md'),
          },
        },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: {
          font: {
            size: getFontSize('md'),
          },
          callback: (value) => `${value}%`,
        },
      },
    },
  }

  const graphData: ComponentProps<typeof Bar>['data'] = {
    labels: [municipalityName, 'Ovägt medel'],
    datasets: data.baselineValues.map((item, index) => ({
      label: item.ageRange,
      data: [item.value, data.comparisonValues[0].mean[index]],
      backgroundColor: getColorByIndex(index).fill,
      borderColor: getColorByIndex(index).border,
      borderWidth: BORDER_WIDTH,
    })),
  }

  const legend = (
    <BenchmarkingLegend items={data.baselineValues.map((value, index) => ({ fillColor: getColorByIndex(index).fill, borderColor: getColorByIndex(index).border, text: value.ageRange }))} />
  )

  const graph: React.ReactNode = <Bar options={graphOptions} data={graphData} />
  const title = 'Andel nya beslut per åldersgrupp'
  const dataSourceSelector = <EmptySelector value={STATDB_NAME} />
  const comparisonSelector = <EmptySelector value={'Ovägt medel'} />
  return <BenchmarkingContentRow graph={graph} text={text} title={title} legend={legend} dataSourceSelector={dataSourceSelector} comparisonSelector={comparisonSelector} />
}
